// src/games/Rebus/api.ts
import type { iRebus } from '../../interfaces/rebus';
import { apiNode } from '../../services/api';
import { RANKING_TIPO, createRankingPosition, isJogoCompleted } from '../core/api';
import { finishTempo } from '../core/session/time';
import type { FinishResult } from '../core/session/useGameSession';
import { normalizeReveladas, rebusCorrectCount, rebusMaxPoints, rebusPoints } from './rules';

export interface RebusProgressRow {
  pontuacao: number | null;
  tempo: number | null;
  resposta: string | null;
  reveladas?: unknown;
}

export interface SaveRebusArgs {
  userId: number;
  jogoId: number;
  solucao: string;
  input: string;
  reveladas: number[];
  seconds: number;
}

export interface RebusLoad {
  rebus: iRebus;
  solucao: string;
  input: string;
  reveladas: number[];
  savedSeconds: number;
  finished: FinishResult | null;
}

export async function getRebus(jogoId: number): Promise<iRebus | null> {
  const { data } = await apiNode.get(`/show_rebus/${jogoId}`);
  return Array.isArray(data) && data[0] ? data[0] : null;
}

export async function getRebusProgress(userId: number, jogoId: number): Promise<RebusProgressRow | null> {
  const { data } = await apiNode.post('/show_progresso_rebus', { id_jogo: jogoId, id_user: userId });
  return Array.isArray(data) && data[0] ? data[0] : null;
}

export async function saveRebusProgress({ userId, jogoId, solucao, input, reveladas, seconds }: SaveRebusArgs): Promise<void> {
  const normalized = normalizeReveladas(reveladas, solucao.length);
  await apiNode.post('/cria_progresso_rebus', {
    id_jogo: jogoId,
    id_user: userId,
    pontuacao: rebusPoints(solucao, input, normalized),
    tempo: Math.max(0, Math.round(seconds)),
    resposta: input,
    reveladas: normalized,
  });
}

/** Fim: progresso com `reveladas` e depois o ranking tipo 5. */
export async function finishRebus(args: SaveRebusArgs): Promise<void> {
  const tempo = finishTempo(args.seconds);
  await saveRebusProgress({ ...args, seconds: tempo });
  await createRankingPosition({
    id_jogo: args.jogoId,
    id_user: args.userId,
    id_tipo: RANKING_TIPO.rebus,
    pontuacao: rebusPoints(args.solucao, args.input, args.reveladas),
    tempo,
  });
}

export function rebusResult(solucao: string, input: string, reveladas: number[]): FinishResult {
  return {
    points: rebusPoints(solucao, input, reveladas),
    maxPoints: rebusMaxPoints(solucao),
    correct: rebusCorrectCount(solucao, input),
    total: solucao.length,
  };
}

/**
 * `null` = jogo sem conteúdo. Erro ao ler o progresso OU o histórico SOBE: não abre em branco
 * (o autosave gravaria por cima) nem reabre jogável um rébus já terminado.
 */
export async function loadRebusGame(jogoId: number, userId?: number): Promise<RebusLoad | null> {
  const [rebus, progress, completed] = await Promise.all([
    getRebus(jogoId),
    userId !== undefined ? getRebusProgress(userId, jogoId) : Promise.resolve(null),
    userId !== undefined ? isJogoCompleted(jogoId, RANKING_TIPO.rebus) : Promise.resolve(false),
  ]);
  if (!rebus) return null;
  const solucao = rebus.solucao ?? '';
  const input = progress?.resposta ?? '';
  const reveladas = normalizeReveladas(progress?.reveladas, solucao.length);
  return {
    rebus,
    solucao,
    input,
    reveladas,
    savedSeconds: Math.max(0, Math.round(progress?.tempo ?? 0)),
    finished: completed ? rebusResult(solucao, input, reveladas) : null,
  };
}
